import { useIntl } from 'react-intl';

import { ProductType } from '@xing-com/crate-common-graphql-types';
import { redirectTo } from '@xing-com/crate-core-router';
import { ErrorState } from '@xing-com/empty-state';
import { GridContainer, GridItem } from '@xing-com/grid';
import { BREAKPOINTS, useMediaListener } from '@xing-com/util';

import { BrandBackground } from '../components/brand-background';
import { PageTitle } from '../components/page-title';
import { PerkList } from '../components/perk-list';
import { ProductCards } from '../components/product-cards';
import { useActivationUrl } from '../hooks/use-activation-url';
import { useCardsCampaign } from '../hooks/use-cards-campaign';
import type { CardsCampaignOptions } from '../hooks/use-cards-campaign';
import { usePerkListDiscovery } from '../hooks/use-perk-list-discovery';
import { createProductCardsData } from '../utils/create-product-cards-data';
import { mapCampaignToCardData } from '../utils/map-campaign-to-card-data';

import { getPlaceholderPerkListData } from './new-design-page.placeholder-data';

type NewDesignPageProps = {
  product: ProductType;
  campaignContext?: CardsCampaignOptions;
};

export const NewDesignPage: React.FC<NewDesignPageProps> = ({
  product,
  campaignContext,
}) => {
  const { campaigns, errors, loading, retry } = useCardsCampaign(
    product,
    campaignContext
  );
  const { formatMessage, formatNumber, locale } = useIntl();
  const cardData = createProductCardsData(
    product,
    {
      premium: mapCampaignToCardData(ProductType.Premium, campaigns?.premium),
      projobs: mapCampaignToCardData(ProductType.Projobs, campaigns?.projobs),
    },
    { formatMessage, formatNumber }
  );
  const activationUrls = useActivationUrl();
  const matchedMedia = useMediaListener();
  const isSmallScreen = matchedMedia === BREAKPOINTS.handheld;
  const { discoverPerks, perkListKey, perkListProduct, perkListRef } =
    usePerkListDiscovery(product);

  const routeCampaign =
    product === ProductType.Premium ? campaigns?.premium : campaigns?.projobs;
  const routeCampaignError =
    product === ProductType.Premium ? errors?.premium : errors?.projobs;

  // The campaign endpoint returns no offer when the user already owns the
  // requested product. Keep the legacy route behaviour for the redesign.
  if (!loading && !routeCampaignError && routeCampaign === null) {
    redirectTo(`/${product}/features`);
    return null;
  }

  const hasCampaignError =
    !loading &&
    (errors?.premium !== undefined ||
      errors?.projobs !== undefined ||
      cardData === null);

  if (hasCampaignError) {
    return (
      <ErrorState
        buttonProps={{
          onClick: () => {
            void retry().catch(() => undefined);
          },
        }}
        size="large"
      />
    );
  }

  // TODO: (WFS-1666) Replace placeholder data with campaign query data and final i18n values.
  const { heading, premiumPerks, proJobsPerks } =
    getPlaceholderPerkListData(locale);

  const perks = {
    [ProductType.Premium]: premiumPerks,
    [ProductType.Projobs]: proJobsPerks,
  };

  return (
    <>
      <BrandBackground />
      <GridContainer variant="wide">
        <GridItem>
          <PageTitle />
        </GridItem>
        <GridItem>
          <ProductCards
            activationUrls={activationUrls}
            data={cardData}
            loading={loading}
            onDiscover={discoverPerks}
            perks={perks}
          />
        </GridItem>
        {!isSmallScreen ? (
          <GridItem span={12} startLarge={3} spanLarge={8}>
            <PerkList
              key={perkListKey}
              ref={perkListRef}
              activationUrls={activationUrls}
              initialProduct={perkListProduct}
              isLoading={false}
              heading={heading}
              premiumPerks={premiumPerks}
              proJobsPerks={proJobsPerks}
            />
          </GridItem>
        ) : null}
      </GridContainer>
    </>
  );
};
